// Lightweight postcode/address lookup service using Mapbox Geocoding API.
export interface AddressLookupResult {
    address1?: string;
    houseNumber?: string;
    street?: string;
    townCity?: string;
    county?: string;
    country?: string;
    postCode?: string;
    display?: string;
}

function getMapboxToken(): string | undefined {
    return import.meta.env?.VITE_MAPBOX_TOKEN as string | undefined;
}

// Query Mapbox Places API for postcode features and return normalized AddressLookupResult[]
export async function lookupPostcodeAddresses(postcode: string): Promise<AddressLookupResult[]> {
    if (!postcode) throw new Error('Invalid postcode');
    const token = getMapboxToken();
    if (!token) throw new Error('Mapbox token not configured. Set VITE_MAPBOX_TOKEN in your env.');

    const cleaned = encodeURIComponent(postcode.trim());
    // Request both address and postcode features and enable autocomplete so partial postcodes
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${cleaned}.json?types=address,postcode&country=gb&autocomplete=true&limit=6&access_token=${token}`;
    const res = await fetch(url);
    if (!res.ok) {
        throw new Error(`Mapbox lookup failed: ${res.status}`);
    }
    const json = await res.json();
    const features = Array.isArray(json?.features) ? json.features : [];

    const mapped = features.map((f: Record<string, unknown>) => {
        const houseNumber = typeof f.address === 'string' ? f.address : undefined;
        const street = typeof f.text === 'string' ? f.text : undefined;
        // Compose a usable Address Line 1: prefer houseNumber + street when available
        const address1 = (houseNumber ? `${houseNumber} ${street || ''}` : street) || undefined;

        let townCity: string | undefined;
        let county: string | undefined;
        let country: string | undefined;
        let postCode: string | undefined;

        if (Array.isArray(f.context)) {
            for (const c of f.context as Array<Record<string, unknown>>) {
                const id = typeof c.id === 'string' ? c.id : '';
                const text = typeof c.text === 'string' ? c.text : undefined;
                if (id.startsWith('place')) townCity = townCity || text;
                if (id.startsWith('region')) county = county || text;
                if (id.startsWith('country')) country = country || text;
                if (id.startsWith('postcode')) postCode = postCode || text;
            }
        }

        // Some Mapbox features put postcode or locality info inside place_name — use as fallback
        if ((!townCity || !county || !country || !postCode) && typeof f.place_name === 'string') {
            const parts = f.place_name.split(',').map((p: string) => p.trim());
            // Example parts: ['10 Downing St, Westminster, London SW1A 2AA, UK'] or similar
            if (!country && parts.length >= 1) country = parts[parts.length - 1];
            // try to extract postcode token inside any part (e.g., 'London SW1A 2AA')
            for (const p of parts) {
                const possible = p.match(/[A-Z]{1,2}\d[\dA-Z]?\s*\d[A-Z]{2}/i);
                if (possible) {
                    postCode = postCode || possible[0];
                }
            }
            if (!townCity && parts.length >= 2) townCity = parts[1];
            if (!county && parts.length >= 3) county = parts[2];
        }

        const displayParts = [address1, townCity, county, postCode, country].filter(Boolean);
        const display = displayParts.join(', ');

        return {
            address1,
            houseNumber,
            street,
            townCity,
            county,
            country,
            postCode,
            display
        } as AddressLookupResult;
    });

    return mapped;
}

// Convenience: return first suggestion or throw if none
export async function lookupPostcode(postcode: string): Promise<AddressLookupResult> {
    const arr = await lookupPostcodeAddresses(postcode);
    if (!arr || arr.length === 0) throw new Error('No address result');
    return arr[0];
}
